/**
 * FrameLoop — fixed-step simulation with a decoupled render rate.
 *
 * Why this exists, in mobile terms: `requestAnimationFrame` on a thermally
 * throttled phone often drops to 30 Hz (or worse, 20 Hz) for stretches.
 * If our predicted simulation is driven by rAF directly, the local ship's
 * physics slows to match — making the player feel sluggish and producing
 * ugly snaps when the server snapshot lands at the "real" position.
 *
 * The loop instead runs the simulator at a fixed `simHz` (60 Hz default),
 * with a per-rAF catch-up budget capped at `maxSubstepsPerFrame` so a long
 * pause can't trigger a 5-second physics avalanche. Render is called once
 * per rAF with the current alpha (interpolation factor between the last two
 * sim states); the renderer can use that for visual smoothing if it wants.
 *
 * Visibility pause:
 *   - On `visibilitychange -> hidden`, we set a `keepalive` mode that drops
 *     the sim to 10 Hz and stops rendering entirely. This preserves anything
 *     the simulator itself wants to do on a timer (e.g., heartbeat acks)
 *     while not draining the GPU on an unseen tab.
 *   - On `visibilitychange -> visible`, we resume normal cadence and emit a
 *     `resume` callback so the caller can ask the network for a fresh snapshot.
 *
 * Pure browser code; the constructor accepts injected timers so it is
 * unit-testable from Node (vitest) without jsdom.
 */

export interface FrameLoopCallbacks {
	/** Advance the simulation by exactly `dt` seconds. Called 0..N times per rAF. */
	step: (dt: number) => void;
	/** Render a frame. `alpha` is the interpolation factor in [0, 1] between
	 *  the previous and current sim states. Not called while hidden. */
	render: (alpha: number) => void;
	/** Optional: called on `visibilitychange -> visible`. */
	onResume?: () => void;
}

export interface FrameLoopOptions {
	simHz?: number;
	keepaliveHz?: number;
	maxSubstepsPerFrame?: number;
	now?: () => number;
	requestAnimationFrame?: (cb: (t: number) => void) => number;
	cancelAnimationFrame?: (id: number) => void;
	setInterval?: (cb: () => void, ms: number) => ReturnType<typeof setInterval>;
	clearInterval?: (id: ReturnType<typeof setInterval>) => void;
	addVisibilityListener?: (cb: () => void) => () => void;
	isHidden?: () => boolean;
}

export class FrameLoop {
	private readonly cbs: FrameLoopCallbacks;
	private readonly simStepMs: number;
	private readonly keepaliveStepMs: number;
	private readonly maxSubsteps: number;
	private readonly now: () => number;
	private readonly raf: (cb: (t: number) => void) => number;
	private readonly caf: (id: number) => void;
	private readonly setIv: (cb: () => void, ms: number) => ReturnType<typeof setInterval>;
	private readonly clearIv: (id: ReturnType<typeof setInterval>) => void;
	private readonly removeVisibilityListener: () => void;
	private readonly isHidden: () => boolean;

	private rafId: number | null = null;
	private keepaliveId: ReturnType<typeof setInterval> | null = null;
	private accumulatorMs = 0;
	private lastFrameMs = 0;
	private running = false;

	constructor(cbs: FrameLoopCallbacks, opts: FrameLoopOptions = {}) {
		this.cbs = cbs;
		const simHz = opts.simHz ?? 60;
		const keepaliveHz = opts.keepaliveHz ?? 10;
		this.simStepMs = 1000 / simHz;
		this.keepaliveStepMs = 1000 / keepaliveHz;
		this.maxSubsteps = opts.maxSubstepsPerFrame ?? 3;
		this.now = opts.now ?? (() => performance.now());
		this.raf =
			opts.requestAnimationFrame ?? ((cb) => requestAnimationFrame(cb));
		this.caf =
			opts.cancelAnimationFrame ?? ((id) => cancelAnimationFrame(id));
		this.setIv =
			opts.setInterval ?? ((cb, ms) => setInterval(cb, ms));
		this.clearIv =
			opts.clearInterval ?? ((id) => clearInterval(id));
		this.isHidden =
			opts.isHidden ??
			(() =>
				typeof document !== "undefined" && document.visibilityState === "hidden");

		const addListener =
			opts.addVisibilityListener ??
			((cb) => {
				if (typeof document === "undefined") return () => {};
				document.addEventListener("visibilitychange", cb);
				return () => document.removeEventListener("visibilitychange", cb);
			});
		this.removeVisibilityListener = addListener(() => this.handleVisibilityChange());
	}

	start(): void {
		if (this.running) return;
		this.running = true;
		this.lastFrameMs = this.now();
		this.accumulatorMs = 0;
		if (this.isHidden()) {
			this.enterKeepalive();
		} else {
			this.enterActive();
		}
	}

	stop(): void {
		this.running = false;
		this.exitActive();
		this.exitKeepalive();
		this.removeVisibilityListener();
	}

	private enterActive(): void {
		this.exitKeepalive();
		const tick = (): void => {
			if (!this.running || this.isHidden()) return;
			const now = this.now();
			let frameMs = now - this.lastFrameMs;
			this.lastFrameMs = now;
			// Clamp catch-up so a long stall doesn't avalanche the sim.
			const maxFrameMs = this.simStepMs * this.maxSubsteps;
			if (frameMs > maxFrameMs) frameMs = maxFrameMs;
			this.accumulatorMs += frameMs;
			let steps = 0;
			while (
				this.accumulatorMs >= this.simStepMs &&
				steps < this.maxSubsteps
			) {
				this.cbs.step(this.simStepMs / 1000);
				this.accumulatorMs -= this.simStepMs;
				steps += 1;
			}
			const alpha = this.accumulatorMs / this.simStepMs;
			this.cbs.render(alpha);
			this.rafId = this.raf(tick);
		};
		this.rafId = this.raf(tick);
	}

	private exitActive(): void {
		if (this.rafId !== null) {
			this.caf(this.rafId);
			this.rafId = null;
		}
	}

	private enterKeepalive(): void {
		this.exitActive();
		this.keepaliveId = this.setIv(() => {
			if (!this.running) return;
			this.cbs.step(this.keepaliveStepMs / 1000);
		}, this.keepaliveStepMs);
	}

	private exitKeepalive(): void {
		if (this.keepaliveId !== null) {
			this.clearIv(this.keepaliveId);
			this.keepaliveId = null;
		}
	}

	private handleVisibilityChange(): void {
		if (!this.running) return;
		if (this.isHidden()) {
			this.enterKeepalive();
		} else {
			this.lastFrameMs = this.now();
			this.accumulatorMs = 0;
			this.enterActive();
			this.cbs.onResume?.();
		}
	}
}
